import {
  REVIEW_MEDIA_MAX_COUNT,
  REVIEW_MEDIA_MAX_FILE_SIZE_BYTES,
  REVIEW_MEDIA_SERVER_MAX_FILE_SIZE_BYTES,
  REVIEW_VIDEO_MAX_DURATION_SECONDS,
  isReviewMediaContentType,
} from '../../../model/review/review';
import type { CreateReviewMediaPresignedUrlRequest, CreateReviewMediaPresignedUrlResponse } from './types';

type PresignRequester = (
  params: CreateReviewMediaPresignedUrlRequest,
) => Promise<CreateReviewMediaPresignedUrlResponse>;

export const REVIEW_MEDIA_UPLOAD_ERROR_MESSAGE =
  "We couldn't upload that file. Please check your connection and try again.";

export const reviewMediaTypeErrorMessage = (fileName: string) =>
  `${fileName} is not a supported file. Upload a JPEG, PNG, WebP or GIF image, or an MP4, MOV or WebM video.`;

export const reviewMediaSizeErrorMessage = (
  fileName: string,
  maxBytes: number = REVIEW_MEDIA_MAX_FILE_SIZE_BYTES,
) => `${fileName} is larger than ${Math.round(maxBytes / 1024 / 1024)}MB.`;

/** Names the way out: the app encodes what it picks, so the clip is not lost. */
export const REVIEW_VIDEO_SIZE_ERROR_MESSAGE = `This browser can only upload videos up to ${Math.round(
  REVIEW_MEDIA_MAX_FILE_SIZE_BYTES / 1024 / 1024,
)}MB. Larger videos upload from the app.`;

export const REVIEW_VIDEO_DURATION_ERROR_MESSAGE = `Videos can be up to ${REVIEW_VIDEO_MAX_DURATION_SECONDS} seconds.`;

/** Counts what is kept as well, because the server caps the review, not the upload. */
export const REVIEW_MEDIA_COUNT_ERROR_MESSAGE = `A review can carry up to ${REVIEW_MEDIA_MAX_COUNT} photos and videos. Remove one before adding another.`;

/**
 * On `fetch`, not the API client: the signature covers the content type, and our auth and reCAPTCHA
 * headers are neither expected nor signed for by storage.
 */
export const uploadReviewMediaFile = async (file: File, requestPresignedUrl: PresignRequester): Promise<string> => {
  // React Native hands over a `{ uri, name, type }` descriptor, not a Blob; a raw PUT needs bytes.
  const body = file instanceof Blob ? file : await (await fetch((file as unknown as { uri: string }).uri)).blob();
  const contentType = file.type || body.type;

  if (!isReviewMediaContentType(contentType)) {
    throw new Error(reviewMediaTypeErrorMessage(file.name));
  }
  // The server's ceiling, not the picker's: guarding the stricter one rejects at submit time a file
  // the picker already took.
  if (body.size > REVIEW_MEDIA_SERVER_MAX_FILE_SIZE_BYTES) {
    throw new Error(reviewMediaSizeErrorMessage(file.name, REVIEW_MEDIA_SERVER_MAX_FILE_SIZE_BYTES));
  }

  const { uploadUrl, mediaUrl } = await requestPresignedUrl({ contentType, sizeBytes: body.size });

  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body,
    // Signed header: a different value, or none, invalidates the signature.
    headers: { 'Content-Type': contentType },
  });

  if (!uploadResponse.ok) {
    throw new Error(REVIEW_MEDIA_UPLOAD_ERROR_MESSAGE);
  }

  return mediaUrl;
};

/** Sequential: five clips at once on mobile data starve each other, and the set is capped at five. */
export const uploadReviewMediaFiles = async (
  files: File[] | undefined,
  requestPresignedUrl: PresignRequester,
  /** Attachments the submission retains; the cap covers those and the new files together. */
  keptMediaCount: number = 0,
): Promise<string[]> => {
  if (!files?.length) {
    return [];
  }

  // Dropping the extras silently uploaded a set the reviewer never sees again and reads as the
  // picker losing files; the server refuses the whole review anyway.
  if (keptMediaCount + files.length > REVIEW_MEDIA_MAX_COUNT) {
    throw new Error(REVIEW_MEDIA_COUNT_ERROR_MESSAGE);
  }

  const uploadedUrls: string[] = [];
  for (const file of files) {
    uploadedUrls.push(await uploadReviewMediaFile(file, requestPresignedUrl));
  }

  return uploadedUrls;
};
